import {
  refundCustomerStatusChipCs,
  type RefundCustomerFacingReadDTO,
  type RefundRequestedQueueReadDTO,
  type TransactionRefundProjectionStatus,
} from 'pi-kiosk-shared';
import { useCustomerFormatters } from '../../../shared/hooks/useCustomerFormatters.js';
import { clampRefundCustomerStatus } from '../refunds/mapRefundCustomerStatus.js';
import {
  isRefundAttemptWithDownloadDTO,
  isRefundRequestedQueueReadDTO,
  refundConfirmationDownloadUrl,
  type RefundAttemptWithDownloadDTO,
} from '../refunds/refundReadTypes.js';

export interface AccountOrderRefundsPanelProps {
  readonly transactionId: number;
  readonly refunds?: readonly RefundCustomerFacingReadDTO[] | null;
  readonly refundStatus?: TransactionRefundProjectionStatus | string | null;
  readonly t: (key: string, opts?: Record<string, unknown>) => string;
}

function statusLabel(
  t: AccountOrderRefundsPanelProps['t'],
  status: 'returned' | 'processing' | 'needs_resolution' | 'request_accepted',
): string {
  if (status === 'returned') {
    return t('account.orders.refunds.returned');
  }
  if (status === 'needs_resolution') {
    return t('account.orders.refunds.needsResolution');
  }
  if (status === 'request_accepted') {
    return t('account.orders.refunds.requestAccepted');
  }
  return t('account.orders.refunds.processing');
}

function methodLabel(
  t: AccountOrderRefundsPanelProps['t'],
  method: string,
): string {
  return t(`account.orders.refunds.method.${method}`, { defaultValue: method });
}

export function AccountOrderRefundsPanel({
  transactionId,
  refunds,
  refundStatus,
  t,
}: AccountOrderRefundsPanelProps): JSX.Element | null {
  const { formatPrice } = useCustomerFormatters();
  const rows = refunds ?? [];
  const queuePlaceholders: readonly RefundRequestedQueueReadDTO[] = rows.filter(
    isRefundRequestedQueueReadDTO,
  );
  const attemptRows: readonly RefundAttemptWithDownloadDTO[] = rows.filter(
    isRefundAttemptWithDownloadDTO,
  );
  const showRequestedQueue =
    (rows.length === 0 && refundStatus === 'REQUESTED') || queuePlaceholders.length > 0;

  if (rows.length === 0 && !showRequestedQueue) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-[var(--color-border)] p-3"
      data-testid={`customer-order-refunds-${transactionId}`}
    >
      <p className="text-sm font-medium text-[var(--color-on-surface)]">
        {t('account.orders.refunds.sectionTitle')}
      </p>
      {showRequestedQueue ? (
        <p
          className="text-sm text-[var(--color-on-surface)]"
          data-testid={`customer-order-refund-status-${transactionId}`}
          data-customer-status="processing"
        >
          {statusLabel(t, 'request_accepted')}
        </p>
      ) : null}
      {attemptRows.map((refund) => {
        const visible = clampRefundCustomerStatus(refund);
        const downloadUrl = refundConfirmationDownloadUrl(refund);
        return (
          <div
            key={refund.attemptId}
            className="flex flex-col gap-1 text-sm text-[var(--color-on-surface)]"
            data-testid={`customer-order-refund-attempt-${refund.attemptId}`}
            data-customer-status={visible}
            data-attempt-status={refund.attemptStatus}
          >
            <p
              data-testid={`customer-refund-amount-${refund.attemptId}`}
              className="font-medium"
            >
              {formatPrice(refund.amountMajor, refund.currency)}
            </p>
            <p data-testid={`customer-refund-method-${refund.attemptId}`}>
              {methodLabel(t, refund.method)}
            </p>
            <p data-testid={`customer-refund-status-${refund.attemptId}`}>
              {statusLabel(t, visible)}
              <span className="sr-only">{refundCustomerStatusChipCs(visible)}</span>
            </p>
            {downloadUrl !== null ? (
              <a
                href={downloadUrl}
                className="text-[var(--color-primary)] underline"
                data-testid={`customer-refund-download-${refund.attemptId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('account.orders.refunds.downloadConfirmation', {
                  defaultValue: 'Download confirmation',
                })}
              </a>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
